import { TextNode, $isTextNode } from 'lexical';

/**
 * TextNode subclass for color, highlight, font family, and font size.
 * Lexical's TextNode reserves __style for a CSS string (cssText). Storing an object there
 * breaks format classes (bold/italic) and splitText. Rich styles live in __richStyle instead.
 */
export class StyledTextNode extends TextNode {
  /** @internal */
  __richStyle;

  constructor(text, style, key) {
    super(text, key);
    this.__richStyle = style && typeof style === 'object' ? { ...style } : {};
  }

  static getType() {
    return 'styled-text';
  }

  static clone(node) {
    const latest = node.getLatest();
    return new StyledTextNode(latest.__text, { ...(latest.__richStyle || {}) }, latest.__key);
  }

  static importJSON(serializedNode) {
    const node = new StyledTextNode(serializedNode.text, serializedNode.style || {});
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    return node;
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'styled-text',
      version: 1,
      style: { ...(this.__richStyle || {}) },
    };
  }

  getRichStyle() {
    const self = this.getLatest();
    return { ...(self.__richStyle || {}) };
  }

  /**
   * Merge rich style object, or delegate CSS string to Lexical's TextNode.
   */
  setStyle(style) {
    if (typeof style === 'string') {
      return super.setStyle(style);
    }
    if (!style || typeof style !== 'object') {
      return this.getLatest();
    }
    const self = this.getWritable();
    self.__richStyle = { ...(self.__richStyle || {}), ...style };
    return self;
  }

  createDOM(config) {
    const dom = super.createDOM(config);
    const rs = this.__richStyle;
    if (rs && typeof rs === 'object') {
      if (rs.color) {
        // eslint-disable-next-line no-param-reassign
        dom.style.color = rs.color;
      }
      if (rs.backgroundColor) {
        // eslint-disable-next-line no-param-reassign
        dom.style.backgroundColor = rs.backgroundColor;
      }
      if (rs.fontFamily) {
        // eslint-disable-next-line no-param-reassign
        dom.style.fontFamily = rs.fontFamily;
      }
      if (rs.fontSize) {
        // eslint-disable-next-line no-param-reassign
        dom.style.fontSize = rs.fontSize;
      }
    }
    return dom;
  }

  updateDOM(prevNode, dom, config) {
    const shouldReplace = super.updateDOM(prevNode, dom, config);
    if (shouldReplace) {
      return true;
    }

    const prevRs = prevNode instanceof StyledTextNode ? prevNode.__richStyle : null;
    const rs = this.__richStyle;

    if (rs && typeof rs === 'object') {
      if (rs.color) {
        // eslint-disable-next-line no-param-reassign
        dom.style.color = rs.color;
      } else if (prevRs?.color) {
        // eslint-disable-next-line no-param-reassign
        dom.style.color = '';
      }

      if (rs.backgroundColor) {
        // eslint-disable-next-line no-param-reassign
        dom.style.backgroundColor = rs.backgroundColor;
      } else if (prevRs?.backgroundColor) {
        // eslint-disable-next-line no-param-reassign
        dom.style.backgroundColor = '';
      }

      if (rs.fontFamily) {
        // eslint-disable-next-line no-param-reassign
        dom.style.fontFamily = rs.fontFamily;
      } else if (prevRs?.fontFamily) {
        // eslint-disable-next-line no-param-reassign
        dom.style.fontFamily = '';
      }

      if (rs.fontSize) {
        // eslint-disable-next-line no-param-reassign
        dom.style.fontSize = rs.fontSize;
      } else if (prevRs?.fontSize) {
        // eslint-disable-next-line no-param-reassign
        dom.style.fontSize = '';
      }
    }

    return false;
  }

  setColor(color) {
    const self = this.getWritable();
    self.__richStyle = { ...(self.__richStyle || {}), color };
    return self;
  }

  getColor() {
    const self = this.getLatest();
    return self.__richStyle?.color;
  }

  setHighlightColor(backgroundColor) {
    const self = this.getWritable();
    self.__richStyle = { ...(self.__richStyle || {}), backgroundColor };
    return self;
  }

  getHighlightColor() {
    const self = this.getLatest();
    return self.__richStyle?.backgroundColor;
  }

  setFontFamily(fontFamily) {
    const self = this.getWritable();
    self.__richStyle = { ...(self.__richStyle || {}), fontFamily };
    return self;
  }

  getFontFamily() {
    const self = this.getLatest();
    return self.__richStyle?.fontFamily;
  }

  setFontSize(fontSize) {
    const self = this.getWritable();
    self.__richStyle = { ...(self.__richStyle || {}), fontSize };
    return self;
  }

  getFontSize() {
    const self = this.getLatest();
    return self.__richStyle?.fontSize;
  }

  splitText(...splitOffsets) {
    const self = this.getLatest();
    const richSnapshot = { ...(self.__richStyle || {}) };
    const shouldPreserveRich = Object.keys(richSnapshot).length > 0;

    const splitNodes = super.splitText(...splitOffsets);

    if (!shouldPreserveRich) {
      return splitNodes;
    }

    return splitNodes.map((node) => {
      if (node instanceof StyledTextNode) {
        const writable = node.getWritable();
        writable.__richStyle = { ...richSnapshot };
        return node.getLatest();
      }
      if ($isTextNode(node)) {
        const replacement = new StyledTextNode(node.getTextContent(), { ...richSnapshot });
        replacement.setFormat(node.getFormat());
        replacement.setDetail(node.getDetail());
        replacement.setMode(node.getMode());
        node.replace(replacement);
        return replacement.getLatest();
      }
      return node;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  canHaveFormat() {
    return true;
  }
}

export function $createStyledTextNode(text = '', style = {}) {
  return new StyledTextNode(text, style);
}

export function $isStyledTextNode(node) {
  return node instanceof StyledTextNode;
}
